import { Route, Routes } from 'react-router';
import { RequireAnyScope, RequireAuth, RequirePermission } from './auth/RequireAuth';
import { AccountPage } from './features/account/AccountPage';
import { AuditPage } from './features/audit/AuditPage';
import { OrganizationPage } from './features/organization/OrganizationPage';
import { SchoolDetailPage } from './features/schools/SchoolDetailPage';
import { SchoolsPage } from './features/schools/SchoolsPage';
import { SettingsPage } from './features/settings/SettingsPage';
import { AcademicsPage } from './features/academics/AcademicsPage';
import { AdmissionsPage } from './features/admissions/AdmissionsPage';
import { LibraryPage } from './features/library/LibraryPage';
import { InboxPage } from './features/comms/InboxPage';
import { CertificatesPage } from './features/documents/CertificatesPage';
import { DocumentsPage } from './features/documents/DocumentsPage';
import { ReportsPage } from './features/reports/ReportsPage';
import { VerifyCertificatePage } from './features/documents/VerifyCertificatePage';
import { NoticesPage } from './features/comms/NoticesPage';
import { HostelPage } from './features/hostel/HostelPage';
import { InventoryPage } from './features/inventory/InventoryPage';
import { TransportPage } from './features/transport/TransportPage';
import { MyLibraryPage } from './features/library/MyLibraryPage';
import { HrPage } from './features/hr/HrPage';
import { MyHrPage } from './features/hr/MyHrPage';
import { AccountingPage } from './features/fees/AccountingPage';
import { FeesPage } from './features/fees/FeesPage';
import { ExamsPage } from './features/exams/ExamsPage';
import { MarksPage } from './features/exams/MarksPage';
import { AttendancePage } from './features/attendance/AttendancePage';
import { LeavePage } from './features/attendance/LeavePage';
import { ChildPage } from './features/portal/ChildPage';
import { MyTimetablePage } from './features/timetable/MyTimetablePage';
import { TimetablePage } from './features/timetable/TimetablePage';
import { MyChildrenPage } from './features/portal/MyChildrenPage';
import { MyClassesPage } from './features/portal/MyClassesPage';
import { StaffPage } from './features/staff/StaffPage';
import { StudentDetailPage } from './features/students/StudentDetailPage';
import { StudentsPage } from './features/students/StudentsPage';
import { UsersPage } from './features/users/UsersPage';
import { AppShell } from './layout/AppShell';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { LoginPage } from './pages/auth/LoginPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { HomeworkPage } from './features/homework/HomeworkPage';
import { MessagesPage } from './features/messages/MessagesPage';
import { DashboardPage } from './pages/DashboardPage';
import { NotFoundPage } from './pages/NotFoundPage';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify" element={<VerifyCertificatePage />} />
      <Route path="/verify/:code" element={<VerifyCertificatePage />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<DashboardPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route
            path="users"
            element={
              <RequirePermission permission="users.view">
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="audit"
            element={
              <RequirePermission permission="audit.view">
                <AuditPage />
              </RequirePermission>
            }
          />
          <Route
            path="schools"
            element={
              <RequireAnyScope permission="schools.view">
                <SchoolsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="schools/:schoolId"
            element={
              <RequireAnyScope permission="schools.view">
                <SchoolDetailPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="settings"
            element={
              <RequireAnyScope permission="settings.view">
                <SettingsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="organization"
            element={
              <RequirePermission permission="orgs.view">
                <OrganizationPage />
              </RequirePermission>
            }
          />
          <Route
            path="students"
            element={
              <RequireAnyScope permission={['students.view', 'students.view_assigned']}>
                <StudentsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="students/:studentId"
            element={
              <RequireAnyScope permission={['students.view', 'students.view_assigned']}>
                <StudentDetailPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="admissions"
            element={
              <RequireAnyScope permission="admissions.view">
                <AdmissionsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="staff"
            element={
              <RequireAnyScope permission="staff.view">
                <StaffPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="my-children"
            element={
              <RequirePermission permission="portal.parent">
                <MyChildrenPage />
              </RequirePermission>
            }
          />
          <Route
            path="my-children/:studentId"
            element={
              <RequirePermission permission="portal.parent">
                <ChildPage />
              </RequirePermission>
            }
          />
          <Route
            path="homework"
            element={
              <RequireAnyScope
                permission={['homework.view_all', 'homework.manage_all', 'homework.manage']}
              >
                <HomeworkPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="messages"
            element={
              <RequirePermission permission="messages.use">
                <MessagesPage />
              </RequirePermission>
            }
          />
          <Route
            path="attendance"
            element={
              <RequireAnyScope permission={['attendance.view', 'attendance.view_assigned']}>
                <AttendancePage />
              </RequireAnyScope>
            }
          />
          <Route
            path="library"
            element={
              <RequireAnyScope permission={['library.view', 'library.view_loans']}>
                <LibraryPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="transport"
            element={
              <RequireAnyScope permission="transport.view">
                <TransportPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="hostel"
            element={
              <RequireAnyScope permission="hostel.view">
                <HostelPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="inventory"
            element={
              <RequireAnyScope permission="inventory.view">
                <InventoryPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="documents"
            element={
              <RequireAnyScope permission={['documents.view', 'documents.staff_view']}>
                <DocumentsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="reports"
            element={
              <RequireAnyScope permission="reports.view">
                <ReportsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="certificates"
            element={
              <RequireAnyScope permission="certificates.view">
                <CertificatesPage />
              </RequireAnyScope>
            }
          />
          <Route path="inbox" element={<InboxPage />} />
          <Route
            path="notices"
            element={
              <RequireAnyScope permission="comms.view">
                <NoticesPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="my-library"
            element={
              <RequirePermission permission="library.view_own">
                <MyLibraryPage />
              </RequirePermission>
            }
          />
          <Route
            path="hr"
            element={
              <RequireAnyScope
                permission={[
                  'staff_attendance.view',
                  'hr_leave.view',
                  'salary.view',
                  'payroll.view',
                ]}
              >
                <HrPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="my-hr"
            element={
              <RequireAnyScope permission={['hr_leave.apply', 'payslips.view_own']}>
                <MyHrPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="fees"
            element={
              <RequireAnyScope permission={['fees.view', 'payments.view']}>
                <FeesPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="accounting"
            element={
              <RequireAnyScope permission="accounting.view">
                <AccountingPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="exams"
            element={
              <RequireAnyScope permission="exams.view">
                <ExamsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="marks"
            element={
              <RequireAnyScope permission={['marks.view', 'marks.view_assigned']}>
                <MarksPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="timetable"
            element={
              <RequireAnyScope permission="timetable.view">
                <TimetablePage />
              </RequireAnyScope>
            }
          />
          <Route
            path="my-timetable"
            element={
              <RequirePermission permission="students.view_assigned">
                <MyTimetablePage />
              </RequirePermission>
            }
          />
          <Route
            path="leave-requests"
            element={
              <RequireAnyScope permission="leave_requests.view">
                <LeavePage />
              </RequireAnyScope>
            }
          />
          <Route
            path="academics"
            element={
              <RequireAnyScope permission="classes.view">
                <AcademicsPage />
              </RequireAnyScope>
            }
          />
          <Route
            path="my-classes"
            element={
              <RequirePermission permission="students.view_assigned">
                <MyClassesPage />
              </RequirePermission>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
